import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clustersAPI } from '../services/api';
import websocket from '../services/websocket';
import { useAuth } from '../context/AuthContext';
import './ClusterDetails.css';

import { NodeManagementView, ProvisionedNodesView } from './cluster-details/ClusterNodes';
import { ImportedClusterView } from './cluster-details/ClusterResources';
import { ClusterAgent } from './cluster-details/ClusterAgent';
import StorageHealth from './cluster-details/StorageHealth';

function isImported(cluster) {
  return cluster.is_imported === true;
}

function ClusterDetails() {
  const { clusterId } = useParams();
  const navigate      = useNavigate();
  const { isSuperAdmin, hasSectionAction } = useAuth();
  const canEditCluster = hasSectionAction('clusters', 'edit');
  const [cluster, setCluster] = useState(null);
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);

  const loadCluster = useCallback(async () => {
    try {
      const res = await clustersAPI.get(clusterId);
      setCluster(res.data);
    } catch (e) {
      console.error('Failed to load cluster', e);
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  // Load node history logs from DB on page load (persisted status_message per node)
  const loadNodeLogs = useCallback(async () => {
    try {
      const res = await clustersAPI.listNodes(clusterId);
      const nodes = res.data || [];
      const history = nodes
        .filter(n => n.status_message)
        .map(n => ({
          timestamp: n.updated_at || new Date().toISOString(),
          message: n.status_message,
          status: (n.status || '').toLowerCase(),
          nodeLabel: n.label,
          fromHistory: true,
        }));
      if (history.length > 0) {
        setLogs(history);
      }
    } catch (e) { /* ignore */ }
  }, [clusterId]);

  useEffect(() => {
    loadCluster();
    loadNodeLogs();
  }, [clusterId]);

  // Open WebSocket for all clusters — imported clusters also need it for
  // node provisioning logs (provision_single_node sends node_status messages).
  useEffect(() => {
    if (!cluster) return;
    websocket.connect(`cluster_${clusterId}`, handleWsUpdate);
    return () => websocket.disconnect(`cluster_${clusterId}`);
  }, [cluster?.id]);

  const handleWsUpdate = (data) => {
    if (data.type === 'status') {
      setCluster(prev => prev ? { ...prev, status: data.status } : prev);
      if (data.message) {
        setLogs(prev => [...prev, {
          timestamp: new Date().toISOString(),
          message: data.message,
          status: data.status,
        }]);
      }
    }
    if (data.type === 'node_status') {
      loadCluster();
      if (data.message) {
        setLogs(prev => [...prev, {
          timestamp: new Date().toISOString(),
          message: data.message,
          status: data.status,
          nodeLabel: data.node_label || null,
        }]);
      }
    }
    if (data.type === 'ingress_status') {
      loadCluster();
      if (data.message) {
        setLogs(prev => [...prev, {
          timestamp: new Date().toISOString(),
          message: data.message,
          status: data.ingress_status,
          nodeLabel: 'ingress',
        }]);
      }
    }
  };

  const handleDeleteCluster = async () => {
    if (!window.confirm(`Delete cluster "${cluster.name}"? This cannot be undone.`)) return;
    try {
      await clustersAPI.delete(clusterId);
      navigate('/clusters');
    } catch (e) {
      alert('Failed to delete: ' + (e.response?.data?.detail || e.message));
    }
  };

  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput]     = useState('');

  const startRename = () => {
    setNameInput(cluster.name);
    setEditingName(true);
  };

  const cancelRename = () => setEditingName(false);

  const saveRename = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === cluster.name) { setEditingName(false); return; }
    try {
      await clustersAPI.update(clusterId, { name: trimmed });
      setCluster(prev => ({ ...prev, name: trimmed }));
      setEditingName(false);
    } catch (e) {
      alert('Rename failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleRenameKey = (e) => {
    if (e.key === 'Enter') saveRename();
    if (e.key === 'Escape') cancelRename();
  };

  const [editingBaseDomain, setEditingBaseDomain] = useState(false);
  const [baseDomainInput, setBaseDomainInput]     = useState('');

  const startEditBaseDomain = () => {
    setBaseDomainInput(cluster.base_domain || '');
    setEditingBaseDomain(true);
  };

  const cancelEditBaseDomain = () => setEditingBaseDomain(false);

  const saveBaseDomain = async () => {
    const trimmed = baseDomainInput.trim();
    if (trimmed === (cluster.base_domain || '')) { setEditingBaseDomain(false); return; }
    try {
      await clustersAPI.update(clusterId, { base_domain: trimmed || null });
      setCluster(prev => ({ ...prev, base_domain: trimmed || null }));
      setEditingBaseDomain(false);
    } catch (e) {
      alert('Failed to update base domain: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleBaseDomainKey = (e) => {
    if (e.key === 'Enter') saveBaseDomain();
    if (e.key === 'Escape') cancelEditBaseDomain();
  };

  const [editingDefaultSC, setEditingDefaultSC] = useState(false);
  const [defaultSCInput, setDefaultSCInput]     = useState('');

  const startEditDefaultSC = () => {
    setDefaultSCInput(cluster.default_storage_class || '');
    setEditingDefaultSC(true);
  };

  const cancelEditDefaultSC = () => setEditingDefaultSC(false);

  const saveDefaultSC = async () => {
    const trimmed = defaultSCInput.trim();
    if (trimmed === (cluster.default_storage_class || '')) { setEditingDefaultSC(false); return; }
    try {
      await clustersAPI.update(clusterId, { default_storage_class: trimmed || null });
      setCluster(prev => ({ ...prev, default_storage_class: trimmed || null }));
      setEditingDefaultSC(false);
    } catch (e) {
      alert('Failed to update default storage class: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDefaultSCKey = (e) => {
    if (e.key === 'Enter') saveDefaultSC();
    if (e.key === 'Escape') cancelEditDefaultSC();
  };

  if (loading) return <div style={{ padding: 40 }}>Loading...</div>;
  if (!cluster) return <div style={{ padding: 40 }}>Cluster not found</div>;

  const imported = isImported(cluster);

  return (
    <div className="cluster-details-container">
      {/* Header */}
      <div className="page-header">
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="button button-secondary" onClick={() => navigate('/clusters')}>← Back</button>
          {isSuperAdmin && (
            <button className="button button-danger" onClick={handleDeleteCluster}>Delete Cluster</button>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {editingName ? (
            <>
              <input
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                onKeyDown={handleRenameKey}
                autoFocus
                style={{ fontSize: 22, fontWeight: 700, padding: '2px 8px', borderRadius: 6, border: '2px solid var(--brand)' }}
              />
              <button className="button button-primary" onClick={saveRename} style={{ padding: '4px 12px' }}>Save</button>
              <button className="button button-secondary" onClick={cancelRename} style={{ padding: '4px 12px' }}>Cancel</button>
            </>
          ) : (
            <>
              <h1 style={{ margin: 0 }}>{cluster.name}</h1>
              {canEditCluster && (
                <button
                  onClick={startRename}
                  title="Rename cluster"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 16, padding: '2px 4px' }}
                >✎</button>
              )}
            </>
          )}
          {imported && (
            <span className="imported-badge">Imported</span>
          )}
        </div>
      </div>

      {/* Cluster info */}
      <div className="card">
        <h2>Cluster Information</h2>
        <div className="info-grid">
          <div className="info-item">
            <label>Environment</label>
            <span className="badge">{cluster.environment}</span>
          </div>
          <div className="info-item">
            <label>Status</label>
            <span className="status-badge" style={{
              background: cluster.status === 'active' ? 'var(--color-success-500)' : cluster.status === 'failed' ? 'var(--color-danger-500)' : 'var(--brand)',
              color: 'white'
            }}>
              {cluster.status}
            </span>
          </div>
          <div className="info-item">
            <label>Kubernetes Version</label>
            <span>{cluster.version || 'N/A'}</span>
          </div>
          <div className="info-item">
            <label>API Server</label>
            <span style={{ fontSize: 12, wordBreak: 'break-all' }}>{cluster.api_server_url || 'N/A'}</span>
          </div>
          <div className="info-item">
            <label>Base Domain</label>
            {editingBaseDomain ? (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  value={baseDomainInput}
                  onChange={e => setBaseDomainInput(e.target.value)}
                  onKeyDown={handleBaseDomainKey}
                  placeholder="e.g. kube.sarv.com"
                  autoFocus
                  style={{ fontSize: 12, padding: '2px 6px', borderRadius: 6, border: '2px solid var(--brand)' }}
                />
                <button className="button button-primary" onClick={saveBaseDomain} style={{ padding: '2px 8px', fontSize: 12 }}>Save</button>
                <button className="button button-secondary" onClick={cancelEditBaseDomain} style={{ padding: '2px 8px', fontSize: 12 }}>Cancel</button>
              </div>
            ) : (
              <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {cluster.base_domain || 'Not set (required to expose apps via Ingress)'}
                {canEditCluster && (
                  <button
                    onClick={startEditBaseDomain}
                    title="Set base domain"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 14, padding: '2px 4px' }}
                  >✎</button>
                )}
              </span>
            )}
          </div>
          <div className="info-item">
            <label>Default Storage Class</label>
            {editingDefaultSC ? (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  value={defaultSCInput}
                  onChange={e => setDefaultSCInput(e.target.value)}
                  onKeyDown={handleDefaultSCKey}
                  placeholder="e.g. local-path"
                  autoFocus
                  style={{ fontSize: 12, padding: '2px 6px', borderRadius: 6, border: '2px solid var(--brand)' }}
                />
                <button className="button button-primary" onClick={saveDefaultSC} style={{ padding: '2px 8px', fontSize: 12 }}>Save</button>
                <button className="button button-secondary" onClick={cancelEditDefaultSC} style={{ padding: '2px 8px', fontSize: 12 }}>Cancel</button>
              </div>
            ) : (
              <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {cluster.default_storage_class || 'Not set (catalog apps fall back to "local-path")'}
                {canEditCluster && (
                  <button
                    onClick={startEditDefaultSC}
                    title="Set default storage class"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 14, padding: '2px 4px' }}
                  >✎</button>
                )}
              </span>
            )}
          </div>
          <div className="info-item">
            <label>Type</label>
            <span>
              {cluster.connection_type === 'agent'
                ? 'Agent tunnel'
                : imported
                  ? 'Imported (kubeconfig)'
                  : 'Provisioned (bare-metal)'
              }
            </span>
          </div>
          {cluster.connection_type === 'agent' && (
            <div className="info-item">
              <label>Agent</label>
              <span className="status-badge" style={{
                background: cluster.agent_status === 'connected' ? 'var(--color-success-500)'
                  : cluster.agent_status === 'disconnected' ? 'var(--color-danger-500)'
                  : 'var(--muted)',
                color: 'white',
              }}>
                {cluster.agent_status || 'never'}
              </span>
            </div>
          )}
          <div className="info-item">
            <label>Created</label>
            <span>{new Date(cluster.created_at).toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Agent / DaemonSet setup.
          - agent mode + not connected: show install instructions prominently
          - imported (kubeconfig) + private IP: show DaemonSet as required
          - imported (kubeconfig) + public IP: show DaemonSet status (auto-installed)
          - agent connected: show compact status only

          NOT rendered for connection_type='rancher'. Those clusters are reached through
          Rancher's own outbound tunnel, so the SarvKube agent is neither installed nor
          needed. This block predates that transport, so it was telling Rancher users to
          install a DaemonSet that does nothing — directly under a Live Resources panel
          already showing 12 nodes and 1291 pods, which reads as a broken cluster. */}
      {cluster.connection_type !== 'rancher' && (
        <ClusterAgent
          clusterId={clusterId}
          agentStatus={cluster.agent_status}
          connectionType={cluster.connection_type}
          networkType={cluster.network_type}
          ingressStatus={cluster.ingress_status}
          agentScope={cluster.agent_scope}
        />
      )}

      {/* Resources — different view per cluster type */}
      {imported ? (
        <>
          <div className="card">
            <h2>Live Resources</h2>
            <ImportedClusterView clusterId={clusterId} hasVpn={!!cluster.vpn_profile_id} />
          </div>
          {/* Backend is Steve-only (storage_health.py) — only Rancher-managed clusters
              can answer this; showing it elsewhere would just be a confusing 404. */}
          {cluster.connection_type === 'rancher' && (
            <StorageHealth clusterId={clusterId} />
          )}
          <NodeManagementView cluster={cluster} clusterId={clusterId} />
          <ProvisionedNodesView cluster={cluster} clusterId={clusterId} onReload={loadCluster} />
        </>
      ) : (
        <ProvisionedNodesView cluster={cluster} clusterId={clusterId} onReload={loadCluster} />
      )}

      {/* Node provisioning console — always shown for clusters with nodes.
          Pre-populated from DB on load, updated live via WebSocket during provisioning. */}
      {logs.length > 0 && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={{ margin: 0 }}>Node Provisioning Console</h2>
            {logs.length > 0 && (
              <button
                onClick={() => setLogs([])}
                style={{ fontSize: 12, background: 'none', border: '1px solid var(--border)',
                  borderRadius: 4, padding: '2px 10px', cursor: 'pointer', color: 'var(--muted)' }}>
                Clear
              </button>
            )}
          </div>
          <div className="logs-container" style={{
            background: 'var(--ink)', borderRadius: 8, padding: 12, maxHeight: 350,
            overflowY: 'auto', fontFamily: 'monospace', fontSize: 12,
          }}>
            {logs.length === 0 ? (
              <div style={{ color: 'var(--muted)' }}>Waiting for provisioning to start...</div>
            ) : logs.map((log, i) => {
              const s = (log.status || '').toLowerCase();
              const color = s.includes('fail') ? 'var(--color-danger-500)'
                : s.includes('ready') || s.includes('success') ? 'var(--color-success-500)'
                : s.includes('install') ? 'var(--brand)'
                : s.includes('connect') ? 'var(--color-warning-500)'
                : 'var(--border)';
              return (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 4, lineHeight: '1.5',
                  opacity: log.fromHistory ? 0.75 : 1 }}>
                  <span style={{ color: 'var(--ink-2)', whiteSpace: 'nowrap', flexShrink: 0, fontSize: 11 }}>
                    {log.fromHistory ? '📋' : new Date(log.timestamp).toLocaleTimeString()}
                  </span>
                  <span style={{ color: color, wordBreak: 'break-word' }}>
                    {log.nodeLabel && <strong style={{ color: 'var(--muted-2)' }}>[{log.nodeLabel}] </strong>}
                    {log.message}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default ClusterDetails;
